/**
 * Apply Changes Engine
 *
 * Orchestrates pushing reclassification changes to the AssetPro backend.
 * Handles the entity hierarchy: Category -> Type -> SubType -> Make -> Model
 * with proper dependency ordering, deduplication, and error recovery.
 *
 * Also handles ASSET REASSIGNMENT: when new_asset_model matches an existing
 * model with a different ID, assets are moved from the source model to the
 * target model instead of renaming/updating the source model.
 */

import { apiGet, apiPost, apiPostFormData, apiPut, apiPutFormData, apiDelete } from './apiAdapter';
import { decodeEntityNames } from './htmlDecode';

// ── Phase labels for UI ──
export const PHASE_LABELS = {
  fetch: 'Fetching current data from AssetPro',
  plan: 'Building execution plan',
  categories: 'Processing categories',
  types: 'Processing asset types',
  subtypes: 'Processing equipment sub-types',
  makes: 'Processing asset makes',
  models: 'Updating asset models',
  assets: 'Reassigning assets between models',
  cleanup: 'Orphaned entities',
  verify: 'Verifying applied changes',
  done: 'Complete',
};

// ── Helper: extract array from varying API response shapes ──
function extractArray(result) {
  if (!result.success) return [];
  const d = result.data;
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.data)) return d.data;
  if (d && Array.isArray(d.models)) return d.models;
  if (d && Array.isArray(d.subTypes)) return d.subTypes;
  if (d && Array.isArray(d.assets)) return d.assets;
  return [];
}

// ── Helper: generate subtype code from name ──
function generateCode(name) {
  const words = name.toUpperCase().replace(/[^A-Z0-9\s]/g, '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 'UNK';
  if (words.length === 1) return words[0].substring(0, 3);
  if (words.length === 2) return words[0].substring(0, 2) + words[1][0];
  return words.slice(0, 3).map(w => w[0]).join('');
}

// ── Helper: extract _id from a reference field (may be string ID or populated object) ──
function extractId(ref) {
  if (!ref) return '';
  if (typeof ref === 'string') return ref;
  return ref._id || '';
}

// ── Helper: build FormData from key-value object ──
function buildFormData(fields) {
  const fd = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    if (value !== undefined && value !== null) {
      fd.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
    }
  }
  return fd;
}

// ── Helper: batch-execute with concurrency and progress ──
async function batchExecute(items, fn, concurrency, onProgress, phaseLabel) {
  const results = [];
  for (let i = 0; i < items.length; i += concurrency) {
    const batch = items.slice(i, i + concurrency);
    const batchResults = await Promise.allSettled(batch.map((item, j) => fn(item, i + j)));
    for (const r of batchResults) {
      results.push(r.status === 'fulfilled' ? r.value : { success: false, error: r.reason?.message || 'Unknown error' });
    }
    if (onProgress) {
      onProgress(phaseLabel, Math.min(i + batch.length, items.length), items.length,
        `${Math.min(i + batch.length, items.length)} of ${items.length}`);
    }
  }
  return results;
}

// ── Classify entity change: CREATE, RENAME, REUSE, or NO_CHANGE ──
function classifyChange(currentName, newName, existingByName, allChanges, currentField, newField) {
  if (!newName) return { action: 'skip' };
  const normNew = newName.toLowerCase().trim();
  const normCurrent = (currentName || '').toLowerCase().trim();

  // No change
  if (normNew === normCurrent) return { action: 'no_change', existingId: existingByName[normCurrent]?._id };

  // Target already exists -> REUSE
  if (existingByName[normNew]) return { action: 'reuse', existingId: existingByName[normNew]._id };

  // Check if current entity can be renamed:
  // ALL models using the current entity must be changing to the same new name
  if (normCurrent && existingByName[normCurrent]) {
    const currentId = existingByName[normCurrent]._id;
    const modelsUsingCurrent = allChanges.filter(c =>
      (c[currentField] || '').toLowerCase().trim() === normCurrent
    );
    const allGoingSamePlace = modelsUsingCurrent.length > 0 && modelsUsingCurrent.every(c =>
      (c[newField] || '').toLowerCase().trim() === normNew
    );
    if (allGoingSamePlace) {
      return { action: 'rename', existingId: currentId };
    }
  }

  // Otherwise -> CREATE
  return { action: 'create' };
}

// ════════════════════════════════════════════════════════════
// ══  MAIN EXPORT: executeApplyChanges
// ════════════════════════════════════════════════════════════

/**
 * @param changes    the accepted reclassification rows
 * @param onProgress (phase, current, total, detail) => void
 * @param options    { cleanupOrphans } - Phase 7 deletes master data and is OFF
 *                   by default in this app. See the phase itself for why.
 */
export async function executeApplyChanges(changes, onProgress, options = {}) {
  const cleanupOrphans = !!options.cleanupOrphans
  const log = {
    startedAt: new Date().toISOString(),
    completedAt: null,
    phases: {
      categories: { created: [], renamed: [], reused: [], errors: [] },
      types: { created: [], renamed: [], reused: [], errors: [] },
      subtypes: { created: [], renamed: [], reused: [], errors: [] },
      makes: { created: [], renamed: [], reused: [], errors: [] },
      models: { updated: [], reassigned: [], skipped: [], errors: [] },
      assets: { reassigned: [], skipped: [], errors: [] },
      cleanup: { deleted: [], errors: [] },
      verify: { passed: [], failed: [], assetsPassed: [], assetsFailed: [], errors: [] },
    },
    status: 'in_progress',
  };

  const progress = (phase, current, total, detail) => {
    if (onProgress) onProgress(phase, current, total, detail);
  };

  // Track source models from reassignment for cleanup
  let reassignSourceModels = null;

  try {
    // ── Phase 0: Fetch current state ──
    progress('fetch', 0, 6, 'Loading current AssetPro data...');

    const opts = { headers: { 'Accept': '*/*' } };
    const [catRes, typeRes, subRes, makeRes, modelRes, assetRes] = await Promise.all([
      apiGet('/asset-classification', opts),
      apiGet('/asset-type', opts),
      apiGet('/equipment-sub-type', opts),
      apiGet('/asset-make', opts),
      apiGet('/asset-model', opts),
      apiGet('/asset/asset-all', opts),
    ]);

    progress('fetch', 6, 6, 'Data loaded');

    // Decode HTML entities in entity names at the ingestion boundary so that
    // all downstream lookups (modelByName, classifyChange, etc.) compare clean
    // strings. The AssetPro backend returns names like "Loader &amp; Backhoe".
    const existingCategories = decodeEntityNames(extractArray(catRes));
    const existingTypes = decodeEntityNames(extractArray(typeRes));
    const existingSubTypes = decodeEntityNames(extractArray(subRes));
    const existingMakes = decodeEntityNames(extractArray(makeRes));
    const existingModels = decodeEntityNames(extractArray(modelRes));
    const existingAssets = extractArray(assetRes);

    // Build lookup maps (by lowercase name -> { _id, ...entity })
    const categoryByName = {};
    existingCategories.forEach(c => { categoryByName[c.name.toLowerCase().trim()] = c; });

    const typeByName = {};
    existingTypes.forEach(t => { typeByName[t.name.toLowerCase().trim()] = t; });

    const subtypeByName = {};
    existingSubTypes.forEach(s => { subtypeByName[s.name.toLowerCase().trim()] = s; });

    const makeByName = {};
    existingMakes.forEach(m => { makeByName[m.name.toLowerCase().trim()] = m; });

    const modelById = {};
    existingModels.forEach(m => { modelById[m._id] = m; });

    // Model-by-name lookup (for detecting reassignment targets)
    const modelByName = {};
    existingModels.forEach(m => { modelByName[m.name.toLowerCase().trim()] = m; });

    // Assets grouped by their model ID
    const assetsByModelId = {};
    existingAssets.forEach(a => {
      const mId = typeof a.assetModel === 'string' ? a.assetModel
        : (a.assetModel?._id || null);
      if (mId) {
        if (!assetsByModelId[mId]) assetsByModelId[mId] = [];
        assetsByModelId[mId].push(a);
      }
    });

    console.log(`[applyEngine] Loaded: ${existingCategories.length} categories, ${existingTypes.length} types, ${existingSubTypes.length} subtypes, ${existingMakes.length} makes, ${existingModels.length} models, ${existingAssets.length} assets`);

    // ── Phase 1: Build execution plan ──
    progress('plan', 0, 1, 'Analyzing changes...');

    // Deduplicate: group changes by unique entity names
    const uniqueCategories = [...new Set(changes.map(c => c.new_category).filter(Boolean))];
    const uniqueTypes = [...new Set(changes.map(c => c.new_type).filter(Boolean))];
    const uniqueSubtypes = [...new Set(changes.map(c => c.new_subtype).filter(Boolean))];
    const uniqueMakes = [...new Set(changes.map(c => c.new_make).filter(Boolean))];

    // Build type->category mapping from changes
    const typeToCategoryMap = {};
    changes.forEach(c => {
      if (c.new_type && c.new_category) {
        typeToCategoryMap[c.new_type.toLowerCase().trim()] = c.new_category;
      }
    });

    // Build subtype->type mapping from changes
    const subtypeToTypeMap = {};
    changes.forEach(c => {
      if (c.new_subtype && c.new_type) {
        subtypeToTypeMap[c.new_subtype.toLowerCase().trim()] = c.new_type;
      }
    });

    // Build subtype->code mapping from changes (user-defined codes take priority)
    const subtypeToCodeMap = {};
    changes.forEach(c => {
      if (c.new_subtype && c.new_subtype_code) {
        subtypeToCodeMap[c.new_subtype.toLowerCase().trim()] = c.new_subtype_code;
      }
    });

    // ── Split changes: MODEL_UPDATE vs ASSET_REASSIGN ──
    // A change is ASSET_REASSIGN when new_asset_model matches an existing
    // model name that has a DIFFERENT _id from the source model.
    const modelUpdateChanges = [];
    const assetReassignChanges = [];

    changes.forEach(c => {
      if (!c.model_id) return;
      const newModelName = (c.new_asset_model || '').toLowerCase().trim();
      const targetModel = newModelName ? modelByName[newModelName] : null;

      if (targetModel && targetModel._id !== c.model_id) {
        // Target model already exists with a different ID -> move assets
        assetReassignChanges.push({
          ...c,
          _sourceModelId: c.model_id,
          _targetModelId: targetModel._id,
          _targetModelName: targetModel.name,
        });
      } else {
        // Normal model update (rename, re-link subtype/make, or no change)
        modelUpdateChanges.push(c);
      }
    });

    console.log(`[applyEngine] Plan: ${modelUpdateChanges.length} model updates, ${assetReassignChanges.length} asset reassignments`);
    progress('plan', 1, 1, 'Plan ready');

    // ── Phase 2: Categories ──
    const resolvedCategories = {}; // name (lowercase) -> _id
    // Seed with existing
    existingCategories.forEach(c => { resolvedCategories[c.name.toLowerCase().trim()] = c._id; });

    if (uniqueCategories.length > 0) {
      progress('categories', 0, uniqueCategories.length, 'Processing categories...');

      for (let i = 0; i < uniqueCategories.length; i++) {
        const catName = uniqueCategories[i];
        const normName = catName.toLowerCase().trim();
        progress('categories', i, uniqueCategories.length, catName);

        const classification = classifyChange(
          null, catName, categoryByName, changes, 'current_category', 'new_category'
        );

        try {
          if (classification.action === 'reuse' || classification.action === 'no_change') {
            resolvedCategories[normName] = classification.existingId;
            log.phases.categories.reused.push({ name: catName, id: classification.existingId });
          } else if (classification.action === 'create') {
            const fd = buildFormData({ name: catName });
            const res = await apiPostFormData('/asset-classification', fd);
            if (!res.success) throw new Error(res.error);
            resolvedCategories[normName] = res.data._id;
            log.phases.categories.created.push({ name: catName, id: res.data._id });
          } else if (classification.action === 'rename') {
            const fd = buildFormData({ name: catName });
            const res = await apiPutFormData(`/asset-classification/${classification.existingId}`, fd);
            if (!res.success) throw new Error(res.error);
            resolvedCategories[normName] = classification.existingId;
            log.phases.categories.renamed.push({ name: catName, id: classification.existingId });
          }
        } catch (err) {
          log.phases.categories.errors.push({ name: catName, error: err.message });
        }
      }
      progress('categories', uniqueCategories.length, uniqueCategories.length, 'Done');
    }

    // ── Phase 3: Types ──
    const resolvedTypes = {}; // name (lowercase) -> _id
    existingTypes.forEach(t => { resolvedTypes[t.name.toLowerCase().trim()] = t._id; });

    if (uniqueTypes.length > 0) {
      progress('types', 0, uniqueTypes.length, 'Processing types...');

      for (let i = 0; i < uniqueTypes.length; i++) {
        const typeName = uniqueTypes[i];
        const normName = typeName.toLowerCase().trim();
        progress('types', i, uniqueTypes.length, typeName);

        const classification = classifyChange(
          // Find the most common current_type for models going to this new_type
          changes.find(c => (c.new_type || '').toLowerCase().trim() === normName)?.current_type,
          typeName, typeByName, changes, 'current_type', 'new_type'
        );

        // Resolve parent category
        const parentCatName = typeToCategoryMap[normName];
        const parentCatId = parentCatName ? resolvedCategories[parentCatName.toLowerCase().trim()] : undefined;

        try {
          if (classification.action === 'reuse' || classification.action === 'no_change') {
            resolvedTypes[normName] = classification.existingId;
            log.phases.types.reused.push({ name: typeName, id: classification.existingId });
            // Update category link if needed
            if (parentCatId) {
              const fd = buildFormData({ name: typeName, assetClassification: parentCatId });
              await apiPutFormData(`/asset-type/${classification.existingId}`, fd);
            }
          } else if (classification.action === 'create') {
            const fields = { name: typeName };
            if (parentCatId) fields.assetClassification = parentCatId;
            const fd = buildFormData(fields);
            const res = await apiPostFormData('/asset-type', fd);
            if (!res.success) throw new Error(res.error);
            resolvedTypes[normName] = res.data._id;
            log.phases.types.created.push({ name: typeName, id: res.data._id });
          } else if (classification.action === 'rename') {
            const fields = { name: typeName };
            if (parentCatId) fields.assetClassification = parentCatId;
            const fd = buildFormData(fields);
            const res = await apiPutFormData(`/asset-type/${classification.existingId}`, fd);
            if (!res.success) throw new Error(res.error);
            resolvedTypes[normName] = classification.existingId;
            log.phases.types.renamed.push({ name: typeName, id: classification.existingId });
          }
        } catch (err) {
          log.phases.types.errors.push({ name: typeName, error: err.message });
        }
      }
      progress('types', uniqueTypes.length, uniqueTypes.length, 'Done');
    }

    // ── Phase 4: SubTypes ──
    const resolvedSubTypes = {}; // name (lowercase) -> _id
    existingSubTypes.forEach(s => { resolvedSubTypes[s.name.toLowerCase().trim()] = s._id; });

    if (uniqueSubtypes.length > 0) {
      progress('subtypes', 0, uniqueSubtypes.length, 'Processing sub-types...');

      for (let i = 0; i < uniqueSubtypes.length; i++) {
        const subName = uniqueSubtypes[i];
        const normName = subName.toLowerCase().trim();
        progress('subtypes', i, uniqueSubtypes.length, subName);

        const classification = classifyChange(
          changes.find(c => (c.new_subtype || '').toLowerCase().trim() === normName)?.current_subtype,
          subName, subtypeByName, changes, 'current_subtype', 'new_subtype'
        );

        // Resolve parent type
        const parentTypeName = subtypeToTypeMap[normName];
        const parentTypeId = parentTypeName ? resolvedTypes[parentTypeName.toLowerCase().trim()] : undefined;

        try {
          if (classification.action === 'reuse' || classification.action === 'no_change') {
            resolvedSubTypes[normName] = classification.existingId;

            // Even when reusing, update the subtype if the code changed or parent type changed
            const existing = subtypeByName[normName];
            const userCode = subtypeToCodeMap[normName];
            const currentCode = existing?.code || '';
            const codeChanged = userCode && userCode.toUpperCase() !== currentCode.toUpperCase();
            const currentTypeId = existing?.assetType
              ? (typeof existing.assetType === 'string' ? existing.assetType : existing.assetType._id)
              : '';
            const typeChanged = parentTypeId && parentTypeId !== currentTypeId;

            if (codeChanged || typeChanged) {
              const fields = { name: subName, code: userCode || currentCode };
              if (parentTypeId) fields.assetType = parentTypeId;
              else fields.assetType = currentTypeId;
              fields.checklist = [];
              fields.service = [];
              fields.documentData = [];
              const fd = buildFormData(fields);
              const res = await apiPutFormData(`/equipment-sub-type/${classification.existingId}`, fd);
              if (!res.success) throw new Error(res.error);
              log.phases.subtypes.reused.push({
                name: subName, id: classification.existingId,
                codeUpdated: codeChanged ? userCode : undefined,
                typeUpdated: typeChanged ? parentTypeId : undefined,
              });
            } else {
              log.phases.subtypes.reused.push({ name: subName, id: classification.existingId });
            }
          } else if (classification.action === 'create') {
            if (!parentTypeId) {
              throw new Error(`Parent type "${parentTypeName}" not resolved - cannot create sub-type`);
            }
            // Use user-defined code if available, otherwise auto-generate
            // (codes can be duplicates — no collision avoidance needed)
            const code = subtypeToCodeMap[normName] || generateCode(subName);

            const fd = buildFormData({
              name: subName,
              assetType: parentTypeId,
              code: code,
              checklist: [],
              service: [],
              documentData: [],
            });
            const res = await apiPostFormData('/equipment-sub-type', fd);
            if (!res.success) throw new Error(res.error);
            resolvedSubTypes[normName] = res.data._id;
            log.phases.subtypes.created.push({ name: subName, code, id: res.data._id });
          } else if (classification.action === 'rename') {
            const existing = subtypeByName[normName] || subtypeByName[(changes.find(c => (c.new_subtype || '').toLowerCase().trim() === normName)?.current_subtype || '').toLowerCase().trim()];
            const fields = { name: subName, code: subtypeToCodeMap[normName] || existing?.code || generateCode(subName) };
            if (parentTypeId) fields.assetType = parentTypeId;
            else if (existing?.assetType) {
              fields.assetType = typeof existing.assetType === 'string' ? existing.assetType : existing.assetType._id;
            }
            fields.checklist = [];
            fields.service = [];
            fields.documentData = [];
            const fd = buildFormData(fields);
            const res = await apiPutFormData(`/equipment-sub-type/${classification.existingId}`, fd);
            if (!res.success) throw new Error(res.error);
            resolvedSubTypes[normName] = classification.existingId;
            log.phases.subtypes.renamed.push({ name: subName, id: classification.existingId });
          }
        } catch (err) {
          log.phases.subtypes.errors.push({ name: subName, error: err.message });
        }
      }
      progress('subtypes', uniqueSubtypes.length, uniqueSubtypes.length, 'Done');
    }

    // ── Phase 5: Makes (uses JSON, not FormData) ──
    const resolvedMakes = {}; // name (lowercase) -> _id
    existingMakes.forEach(m => { resolvedMakes[m.name.toLowerCase().trim()] = m._id; });

    if (uniqueMakes.length > 0) {
      progress('makes', 0, uniqueMakes.length, 'Processing makes...');

      for (let i = 0; i < uniqueMakes.length; i++) {
        const makeName = uniqueMakes[i];
        const normName = makeName.toLowerCase().trim();
        progress('makes', i, uniqueMakes.length, makeName);

        const classification = classifyChange(
          changes.find(c => (c.new_make || '').toLowerCase().trim() === normName)?.current_make,
          makeName, makeByName, changes, 'current_make', 'new_make'
        );

        try {
          if (classification.action === 'reuse' || classification.action === 'no_change') {
            resolvedMakes[normName] = classification.existingId;
            log.phases.makes.reused.push({ name: makeName, id: classification.existingId });
          } else if (classification.action === 'create') {
            const res = await apiPost('/asset-make', { name: makeName });
            if (!res.success) throw new Error(res.error);
            resolvedMakes[normName] = res.data._id;
            log.phases.makes.created.push({ name: makeName, id: res.data._id });
          } else if (classification.action === 'rename') {
            const res = await apiPut(`/asset-make/${classification.existingId}`, { name: makeName });
            if (!res.success) throw new Error(res.error);
            resolvedMakes[normName] = classification.existingId;
            log.phases.makes.renamed.push({ name: makeName, id: classification.existingId });
          }
        } catch (err) {
          log.phases.makes.errors.push({ name: makeName, error: err.message });
        }
      }
      progress('makes', uniqueMakes.length, uniqueMakes.length, 'Done');
    }

    // ── Phase 6: Update Asset Models (MODEL_UPDATE changes only) ──
    // Filter to changes that have at least a resolved subtype or make
    const filteredModelUpdates = modelUpdateChanges.filter(c => {
      const subId = resolvedSubTypes[(c.new_subtype || '').toLowerCase().trim()];
      const makeId = resolvedMakes[(c.new_make || '').toLowerCase().trim()];
      return subId || makeId;
    });

    if (filteredModelUpdates.length > 0) {
      progress('models', 0, filteredModelUpdates.length, 'Updating models...');

      await batchExecute(filteredModelUpdates, async (change) => {
        const modelId = change.model_id;
        const existing = modelById[modelId] || {};

        const subId = resolvedSubTypes[(change.new_subtype || '').toLowerCase().trim()];
        const makeId = resolvedMakes[(change.new_make || '').toLowerCase().trim()];

        if (!subId && !makeId) {
          log.phases.models.skipped.push({ modelId, name: change.current_model_name, reason: 'No resolved subtype or make' });
          return { success: false, skipped: true };
        }

        try {

        // Build FormData preserving existing model fields
        const fields = {};
        fields.name = change.new_asset_model || existing.name || '';
        if (subId) fields.assetSubType = subId;
        else if (existing.assetSubType) {
          fields.assetSubType = typeof existing.assetSubType === 'string' ? existing.assetSubType : existing.assetSubType._id;
        }
        if (makeId) fields.assetMake = makeId;
        else if (existing.assetMake) {
          fields.assetMake = typeof existing.assetMake === 'string' ? existing.assetMake : existing.assetMake._id;
        }
        if (change.new_model_no !== undefined && change.new_model_no !== null) fields.modelNumber = change.new_model_no;
        else if (existing.modelNumber !== undefined && existing.modelNumber !== null) fields.modelNumber = existing.modelNumber;

        // Preserve other existing fields. Use explicit null/undefined checks
        // (not truthy) so that legitimate values like 0 or empty string are
        // not silently dropped on the rebuilt PUT payload.
        if (existing.code !== undefined && existing.code !== null) fields.code = existing.code;
        if (existing.isCountable !== undefined) fields.isCountable = existing.isCountable;
        if (existing.isTagDevice !== undefined) fields.isTagDevice = existing.isTagDevice;
        if (existing.isFuelSensorDevice !== undefined) fields.isFuelSensorDevice = existing.isFuelSensorDevice;
        if (existing.acceptableIdleTime !== undefined) fields.acceptableIdleTime = existing.acceptableIdleTime;
        if (existing.idealFuelConsumptionRate !== undefined) fields.idealFuelConsumptionRate = existing.idealFuelConsumptionRate;
        if (existing.fuelType !== undefined && existing.fuelType !== null) {
          fields.fuelType = typeof existing.fuelType === 'string' ? existing.fuelType : existing.fuelType._id;
        }
        // Server expects `meters` as a single comma-separated string of ids,
        // e.g. "id1,id2". Append directly to the FormData below so we don't
        // round-trip through buildFormData (which would otherwise be fine,
        // but we want to keep the encoding explicit for clarity).
        const meterIds = Array.isArray(existing.meters)
          ? existing.meters.map(m => typeof m === 'string' ? m : m._id).filter(Boolean)
          : [];
        // Normalize SCR / mileage threshold to the compact shape the server
        // expects on PUT. The API GET returns these with populated perType
        // objects and extra _id fields; sending that shape back causes the
        // server to silently drop the values.
        const normalizePerType = (entry, valueKey) => {
          if (!entry || typeof entry !== 'object') return null;
          const pt = entry.perType;
          const perType = pt && typeof pt === 'object' ? pt._id : pt;
          if (!perType) return null;
          const out = { perType };
          if (entry[valueKey] !== undefined && entry[valueKey] !== null) {
            out[valueKey] = entry[valueKey];
          }
          return out;
        };
        if (Array.isArray(existing.standardFuelConsumptionRate)) {
          const scr = existing.standardFuelConsumptionRate
            .map(e => normalizePerType(e, 'fuelQty'))
            .filter(Boolean);
          if (scr.length > 0) fields.standardFuelConsumptionRate = scr;
        }
        if (Array.isArray(existing.fuelMileageThreshold)) {
          const fmt = existing.fuelMileageThreshold
            .map(e => normalizePerType(e, 'value'))
            .filter(Boolean);
          if (fmt.length > 0) fields.fuelMileageThreshold = fmt;
        }
        fields.documentData = [];
        fields.documentIds = [];

        const fd = buildFormData(fields);
        // meters: comma-separated single field (server contract)
        if (meterIds.length > 0) fd.append('meters', meterIds.join(','));
        const res = await apiPutFormData(`/asset-model/${modelId}`, fd);
        if (!res.success) throw new Error(res.error);

        log.phases.models.updated.push({ modelId, name: change.new_asset_model || change.current_model_name });
        return res;
        } catch (err) {
          log.phases.models.errors.push({
            modelId,
            name: change.new_asset_model || change.current_model_name,
            error: err.message,
          });
          return { success: false, error: err.message };
        }
      }, 10, (phase, current, total, detail) => {
        progress('models', current, total, detail);
      }, 'models');
    }

    // ── Phase 6a: Cascade equipmentSubType to assets of updated models ──
    // When a model's subtype changes, all assets under that model still point
    // to the old equipmentSubType. Update them to match the new subtype.
    const assetCascadeList = [];
    for (const change of filteredModelUpdates) {
      const subId = resolvedSubTypes[(change.new_subtype || '').toLowerCase().trim()];
      if (!subId) continue;
      const modelId = change.model_id;
      const modelAssets = assetsByModelId[modelId] || [];
      for (const asset of modelAssets) {
        const currentSubId = extractId(asset.equipmentSubType);
        if (currentSubId !== subId) {
          assetCascadeList.push({ asset, modelId, newSubId: subId, modelName: change.new_asset_model || change.current_model_name });
        }
      }
    }

    if (assetCascadeList.length > 0) {
      progress('models', 0, assetCascadeList.length, 'Cascading subtype to assets...');

      await batchExecute(assetCascadeList, async (item) => {
        try {
          const asset = item.asset;
          const fd = buildFormData({
            assetModel: item.modelId,
            equipmentSubType: item.newSubId,
            assetCode: asset.assetCode || '',
            ownerCode: asset.ownerCode || '',
            rate: asset.rate !== undefined ? asset.rate : 1,
            location: extractId(asset.location),
            client: extractId(asset.client),
            owner: extractId(asset.owner),
            leasableInfo: asset.leasableInfo || '[object Object]',
            speedLimit: asset.speedLimit !== undefined ? asset.speedLimit : 30,
            documentData: [],
            documentIds: [],
          });

          const res = await apiPutFormData(`/asset/${asset._id}`, fd);
          if (!res.success) throw new Error(res.error);

          if (!log.phases.models.cascadedAssets) log.phases.models.cascadedAssets = [];
          log.phases.models.cascadedAssets.push({
            assetId: asset._id,
            assetCode: asset.assetCode || '',
            model: item.modelName,
            newSubTypeId: item.newSubId,
          });
          return res;
        } catch (err) {
          log.phases.models.errors.push({
            type: 'asset_cascade',
            assetId: item.asset._id,
            assetCode: item.asset.assetCode || '',
            error: err.message,
          });
          return { success: false, error: err.message };
        }
      }, 10, (phase, current, total, detail) => {
        progress('models', current, total, `Cascading subtype to assets (${current}/${total})...`);
      }, 'asset_cascade');
    }

    // Log reassigned models (they'll be handled in the assets phase)
    for (const c of assetReassignChanges) {
      const sourceAssets = assetsByModelId[c._sourceModelId] || [];
      log.phases.models.reassigned.push({
        modelId: c._sourceModelId,
        sourceModelId: c._sourceModelId,
        name: c.current_model_name,
        targetName: c._targetModelName,
        targetId: c._targetModelId,
        assetCount: sourceAssets.length,
      });
    }

    // ── Phase 6b: Asset Reassignment ──
    // For changes where new_asset_model matches an existing different model,
    // move all assets from the source model to the target model.
    if (assetReassignChanges.length > 0) {
      // Build tracking per source model (for cleanup decisions)
      reassignSourceModels = new Map();

      // Collect all assets to move
      const assetsToMove = [];
      for (const c of assetReassignChanges) {
        const sourceAssets = assetsByModelId[c._sourceModelId] || [];

        reassignSourceModels.set(c._sourceModelId, {
          name: c.current_model_name,
          targetModelId: c._targetModelId,
          targetName: c._targetModelName,
          totalAssets: sourceAssets.length,
          movedCount: 0,
        });

        for (const asset of sourceAssets) {
          assetsToMove.push({
            assetId: asset._id,
            assetCode: asset.assetCode || asset.code || '',
            assetName: asset.name || '',
            sourceModelId: c._sourceModelId,
            sourceModelName: c.current_model_name,
            targetModelId: c._targetModelId,
            targetModelName: c._targetModelName,
            _assetData: asset, // full asset object for building PUT payload
          });
        }
      }

      if (assetsToMove.length > 0) {
        progress('assets', 0, assetsToMove.length, 'Reassigning assets to target models...');

        await batchExecute(assetsToMove, async (item) => {
          try {
            const asset = item._assetData;

            // Resolve target model's subtype so the asset links correctly
            const targetModel = modelById[item.targetModelId];
            const targetSubTypeId = targetModel
              ? extractId(targetModel.assetSubType)
              : extractId(asset.equipmentSubType);

            // Build FormData preserving all existing asset fields
            // (API requires full payload — partial updates lose fields)
            const fd = buildFormData({
              assetModel: item.targetModelId,
              equipmentSubType: targetSubTypeId,
              assetCode: asset.assetCode || '',
              ownerCode: asset.ownerCode || '',
              rate: asset.rate !== undefined ? asset.rate : 1,
              location: extractId(asset.location),
              client: extractId(asset.client),
              owner: extractId(asset.owner),
              leasableInfo: asset.leasableInfo || '[object Object]',
              speedLimit: asset.speedLimit !== undefined ? asset.speedLimit : 30,
              documentData: [],
              documentIds: [],
            });

            const res = await apiPutFormData(`/asset/${item.assetId}`, fd);
            if (!res.success) throw new Error(res.error);

            log.phases.assets.reassigned.push({
              assetId: item.assetId,
              assetCode: item.assetCode,
              assetName: item.assetName,
              from: item.sourceModelName,
              to: item.targetModelName,
            });

            // Track success per source model
            const tracker = reassignSourceModels.get(item.sourceModelId);
            if (tracker) tracker.movedCount++;

            return res;
          } catch (err) {
            log.phases.assets.errors.push({
              assetId: item.assetId,
              assetCode: item.assetCode,
              error: err.message,
            });
            return { success: false, error: err.message };
          }
        }, 10, (phase, current, total, detail) => {
          progress('assets', current, total, detail);
        }, 'assets');
      } else {
        // Reassign changes exist but source models have 0 assets
        for (const c of assetReassignChanges) {
          log.phases.assets.skipped.push({
            sourceModelId: c._sourceModelId,
            sourceModelName: c.current_model_name,
            reason: 'Source model has no assets to move',
          });
        }
        progress('assets', 1, 1, 'No assets to move');
      }
    }

    // ── Phase 7: Full cascade cleanup of orphaned entities ──
    // Bottom-up: models → subtypes → types → categories, plus makes.
    //
    // OFF BY DEFAULT. This deletes master data whose only evidence of being
    // unused is that no asset-model referenced it in the data THIS RUN fetched.
    // Contracts, templates, PV records and anything else that can point at an
    // asset type are not consulted, so a type that is merely unused today - or
    // referenced by something outside this query - would be deleted silently.
    //
    // Callers opt in with { cleanupOrphans: true }, and the UI only offers that
    // after showing exactly what would go, with its reference count.
    if (!cleanupOrphans) {
      progress('cleanup', 6, 6, 'Skipped — orphan cleanup is off');
      log.phases.cleanup.skipped = true;
    } else {
    progress('cleanup', 0, 6, 'Re-fetching current state for cleanup...');

    // Delete orphaned source models from asset reassignment FIRST
    if (reassignSourceModels) {
      for (const [modelId, info] of reassignSourceModels.entries()) {
        if (info.movedCount >= info.totalAssets) {
          try {
            const res = await apiDelete(`/asset-model/${modelId}`);
            if (res.success) {
              log.phases.cleanup.deleted.push({ type: 'model', id: modelId, name: info.name });
            }
          } catch (err) {
            log.phases.cleanup.errors.push({ type: 'model', id: modelId, error: err.message });
          }
        } else {
          console.warn(`[applyEngine] Skipping deletion of model "${info.name}" (${modelId}): only ${info.movedCount}/${info.totalAssets} assets moved`);
        }
      }
    }

    // Re-fetch all entity data to see the current state after all updates
    const [freshModelsRes, freshSubsRes, freshTypesRes, freshCatsRes, freshMakesRes] = await Promise.all([
      apiGet('/asset-model', opts),
      apiGet('/equipment-sub-type', opts),
      apiGet('/asset-type', opts),
      apiGet('/asset-classification', opts),
      apiGet('/asset-make', opts),
    ]);
    const freshModels = decodeEntityNames(extractArray(freshModelsRes));
    const freshSubs = decodeEntityNames(extractArray(freshSubsRes));
    const freshTypes = decodeEntityNames(extractArray(freshTypesRes));
    const freshCats = decodeEntityNames(extractArray(freshCatsRes));
    const freshMakes = decodeEntityNames(extractArray(freshMakesRes));

    console.log(`[applyEngine] Cleanup scan: ${freshModels.length} models, ${freshSubs.length} subtypes, ${freshTypes.length} types, ${freshCats.length} categories, ${freshMakes.length} makes`);

    // Step 1: Count model → subtype and model → make references
    const subtypeRefCount = {};
    const makeRefCount = {};
    freshModels.forEach(m => {
      const subId = extractId(m.assetSubType);
      const mkId = extractId(m.assetMake);
      if (subId) subtypeRefCount[subId] = (subtypeRefCount[subId] || 0) + 1;
      if (mkId) makeRefCount[mkId] = (makeRefCount[mkId] || 0) + 1;
    });

    // Step 2: Delete orphaned subtypes (zero models referencing them)
    progress('cleanup', 1, 6, 'Cleaning orphaned sub-types...');
    const deletedSubtypeIds = new Set();
    let deletedCount = 0;
    for (const s of freshSubs) {
      if (!subtypeRefCount[s._id]) {
        try {
          const res = await apiDelete(`/equipment-sub-type/${s._id}`);
          if (res.success) {
            log.phases.cleanup.deleted.push({ type: 'subtype', id: s._id, name: s.name });
            deletedSubtypeIds.add(s._id);
            deletedCount++;
          }
        } catch (err) {
          log.phases.cleanup.errors.push({ type: 'subtype', id: s._id, name: s.name, error: err.message });
        }
      }
    }

    // Step 3: Count subtype → type references (excluding deleted subtypes)
    const typeRefCount = {};
    freshSubs.forEach(s => {
      if (deletedSubtypeIds.has(s._id)) return;
      const typeId = extractId(s.assetType);
      if (typeId) typeRefCount[typeId] = (typeRefCount[typeId] || 0) + 1;
    });

    // Step 4: Delete orphaned types (zero subtypes referencing them)
    progress('cleanup', 2, 6, 'Cleaning orphaned asset types...');
    const deletedTypeIds = new Set();
    for (const t of freshTypes) {
      if (!typeRefCount[t._id]) {
        try {
          const res = await apiDelete(`/asset-type/${t._id}`);
          if (res.success) {
            log.phases.cleanup.deleted.push({ type: 'type', id: t._id, name: t.name });
            deletedTypeIds.add(t._id);
            deletedCount++;
          }
        } catch (err) {
          log.phases.cleanup.errors.push({ type: 'type', id: t._id, name: t.name, error: err.message });
        }
      }
    }

    // Step 5: Count type → category references (excluding deleted types)
    const categoryRefCount = {};
    freshTypes.forEach(t => {
      if (deletedTypeIds.has(t._id)) return;
      const catId = extractId(t.assetClassification);
      if (catId) categoryRefCount[catId] = (categoryRefCount[catId] || 0) + 1;
    });

    // Step 6: Delete orphaned categories (zero types referencing them)
    progress('cleanup', 3, 6, 'Cleaning orphaned categories...');
    for (const c of freshCats) {
      if (!categoryRefCount[c._id]) {
        try {
          const res = await apiDelete(`/asset-classification/${c._id}`);
          if (res.success) {
            log.phases.cleanup.deleted.push({ type: 'category', id: c._id, name: c.name });
            deletedCount++;
          }
        } catch (err) {
          log.phases.cleanup.errors.push({ type: 'category', id: c._id, name: c.name, error: err.message });
        }
      }
    }

    // Step 7: Delete orphaned makes (zero models referencing them)
    progress('cleanup', 4, 6, 'Cleaning orphaned makes...');
    for (const mk of freshMakes) {
      if (!makeRefCount[mk._id]) {
        try {
          const res = await apiDelete(`/asset-make/${mk._id}`);
          if (res.success) {
            log.phases.cleanup.deleted.push({ type: 'make', id: mk._id, name: mk.name });
            deletedCount++;
          }
        } catch (err) {
          log.phases.cleanup.errors.push({ type: 'make', id: mk._id, name: mk.name, error: err.message });
        }
      }
    }

    progress('cleanup', 6, 6, deletedCount > 0 ? `Deleted ${deletedCount} orphaned entities` : 'No orphans found');
    console.log(`[applyEngine] Cleanup complete: ${deletedCount} entities deleted`);
    }

    // ── Phase 8: Verification — re-fetch and compare against intended changes ──
    progress('verify', 0, 1, 'Re-fetching data for verification...');
    try {
      const [vModelRes, vSubRes, vTypeRes, vCatRes, vMakeRes, vAssetRes] = await Promise.all([
        apiGet('/asset-model', opts),
        apiGet('/equipment-sub-type', opts),
        apiGet('/asset-type', opts),
        apiGet('/asset-classification', opts),
        apiGet('/asset-make', opts),
        apiGet('/asset/asset-all', opts),
      ]);

      const vModels = decodeEntityNames(extractArray(vModelRes));
      const vSubs = decodeEntityNames(extractArray(vSubRes));
      const vTypes = decodeEntityNames(extractArray(vTypeRes));
      const vCats = decodeEntityNames(extractArray(vCatRes));
      const vMakes = decodeEntityNames(extractArray(vMakeRes));
      const vAssets = extractArray(vAssetRes);

      // Build lookup maps from fresh data
      const vModelById = {};
      vModels.forEach(m => { vModelById[m._id] = m; });
      const vSubById = {};
      vSubs.forEach(s => { vSubById[s._id] = s; });
      const vTypeById = {};
      vTypes.forEach(t => { vTypeById[t._id] = t; });
      const vCatById = {};
      vCats.forEach(c => { vCatById[c._id] = c; });
      const vMakeById = {};
      vMakes.forEach(m => { vMakeById[m._id] = m; });

      // Helper to resolve a model's full hierarchy from live data
      const resolveModel = (m) => {
        const subId = extractId(m.assetSubType);
        const sub = vSubById[subId];
        const typeId = sub ? extractId(sub.assetType) : '';
        const typ = vTypeById[typeId];
        const catId = typ ? extractId(typ.assetClassification) : '';
        const cat = vCatById[catId];
        const mkId = extractId(m.assetMake);
        const mk = vMakeById[mkId];
        return {
          name: m.name || '',
          subtype: sub?.name || '',
          subtypeCode: sub?.code || '',
          type: typ?.name || '',
          category: cat?.name || '',
          make: mk?.name || '',
          modelNumber: m.modelNumber || '',
          subtypeId: subId,
        };
      };

      // Verify each change that was applied
      const allAppliedChanges = [...(filteredModelUpdates || []), ...(assetReassignChanges || [])];
      const totalVerify = allAppliedChanges.length;

      for (let vi = 0; vi < allAppliedChanges.length; vi++) {
        const change = allAppliedChanges[vi];
        const modelId = change.model_id || change._targetModelId;
        const liveModel = vModelById[modelId];
        progress('verify', vi + 1, totalVerify + 1, `Verifying model ${vi + 1}/${totalVerify}...`);

        if (!liveModel) {
          // Model was reassigned and source was deleted — check target instead
          if (change._targetModelId && vModelById[change._targetModelId]) {
            // Already covered by target check
            continue;
          }
          log.phases.verify.failed.push({
            modelId,
            name: change.new_asset_model || change.current_model_name,
            issue: 'Model not found in AssetPro after apply',
          });
          continue;
        }

        const live = resolveModel(liveModel);
        const issues = [];

        // Check model name
        const expectedName = change.new_asset_model || change.current_model_name || '';
        if (expectedName && live.name.toLowerCase().trim() !== expectedName.toLowerCase().trim()) {
          issues.push(`Name: expected "${expectedName}", got "${live.name}"`);
        }
        // Check subtype
        if (change.new_subtype && live.subtype.toLowerCase().trim() !== change.new_subtype.toLowerCase().trim()) {
          issues.push(`SubType: expected "${change.new_subtype}", got "${live.subtype}"`);
        }
        // Check subtype code
        if (change.new_subtype_code && live.subtypeCode.toLowerCase().trim() !== change.new_subtype_code.toLowerCase().trim()) {
          issues.push(`SubType Code: expected "${change.new_subtype_code}", got "${live.subtypeCode}"`);
        }
        // Check type
        if (change.new_type && live.type.toLowerCase().trim() !== change.new_type.toLowerCase().trim()) {
          issues.push(`Type: expected "${change.new_type}", got "${live.type}"`);
        }
        // Check category
        if (change.new_category && live.category.toLowerCase().trim() !== change.new_category.toLowerCase().trim()) {
          issues.push(`Category: expected "${change.new_category}", got "${live.category}"`);
        }
        // Check make
        if (change.new_make && live.make.toLowerCase().trim() !== change.new_make.toLowerCase().trim()) {
          issues.push(`Make: expected "${change.new_make}", got "${live.make}"`);
        }

        if (issues.length > 0) {
          log.phases.verify.failed.push({
            modelId,
            name: live.name || expectedName,
            issues,
          });
        } else {
          log.phases.verify.passed.push({
            modelId,
            name: live.name,
          });
        }
      }

      // Verify assets: check equipmentSubType matches their model's assetSubType
      const vAssetsByModelId = {};
      vAssets.forEach(a => {
        const mId = typeof a.assetModel === 'string' ? a.assetModel : a.assetModel?._id;
        if (mId) {
          if (!vAssetsByModelId[mId]) vAssetsByModelId[mId] = [];
          vAssetsByModelId[mId].push(a);
        }
      });

      // Check assets for all models that were updated or received reassigned assets
      const modelsToVerifyAssets = new Set();
      for (const c of (filteredModelUpdates || [])) modelsToVerifyAssets.add(c.model_id);
      for (const c of (assetReassignChanges || [])) {
        if (c._targetModelId) modelsToVerifyAssets.add(c._targetModelId);
      }

      for (const mid of modelsToVerifyAssets) {
        const liveModel = vModelById[mid];
        if (!liveModel) continue;
        const expectedSubId = extractId(liveModel.assetSubType);
        const modelAssets = vAssetsByModelId[mid] || [];
        for (const asset of modelAssets) {
          const assetSubId = extractId(asset.equipmentSubType);
          if (expectedSubId && assetSubId !== expectedSubId) {
            log.phases.verify.assetsFailed.push({
              assetId: asset._id,
              assetCode: asset.assetCode || '',
              model: liveModel.name || mid,
              expectedSubType: expectedSubId,
              actualSubType: assetSubId,
              issue: `Asset equipmentSubType (${assetSubId}) does not match model assetSubType (${expectedSubId})`,
            });
          } else {
            log.phases.verify.assetsPassed.push({
              assetId: asset._id,
              assetCode: asset.assetCode || '',
              model: liveModel.name || mid,
            });
          }
        }
      }

      const modelFails = log.phases.verify.failed.length;
      const assetFails = log.phases.verify.assetsFailed.length;
      const totalPassed = log.phases.verify.passed.length + log.phases.verify.assetsPassed.length;
      const totalFailed = modelFails + assetFails;

      if (totalFailed > 0) {
        console.warn(`[applyEngine] Verification: ${totalFailed} issues found (${modelFails} model, ${assetFails} asset)`);
        progress('verify', totalVerify + 1, totalVerify + 1, `Verification: ${totalFailed} issue(s) found`);
      } else {
        console.log(`[applyEngine] Verification: All ${totalPassed} checks passed`);
        progress('verify', totalVerify + 1, totalVerify + 1, `Verified: all ${totalPassed} checks passed`);
      }
    } catch (verifyErr) {
      console.error('[applyEngine] Verification failed:', verifyErr);
      log.phases.verify.errors.push({ error: verifyErr.message });
      progress('verify', 1, 1, `Verification error: ${verifyErr.message}`);
    }

    // ── Done ──
    log.completedAt = new Date().toISOString();
    const totalErrors = Object.values(log.phases).reduce((sum, p) => sum + (p.errors?.length || 0), 0);
    log.status = totalErrors === 0 ? 'completed' : 'partial_failure';

    progress('done', 1, 1, log.status === 'completed' ? 'All changes applied successfully' : `Completed with ${totalErrors} error(s)`);

    return log;

  } catch (err) {
    log.completedAt = new Date().toISOString();
    log.status = 'failed';
    log.fatalError = err.message;
    progress('done', 0, 1, `Fatal error: ${err.message}`);
    return log;
  }
}

/**
 * Repair Asset SubTypes
 *
 * Standalone utility that syncs every asset's equipmentSubType to match
 * its model's assetSubType. Fixes stale references without re-running
 * the full reclassification pipeline.
 *
 * @param {Function} onProgress - (phase, current, total, detail) callback
 * @returns {Object} repair log with fixed/skipped/error counts
 */
export async function repairAssetSubTypes(onProgress) {
  const progress = (phase, current, total, detail) => {
    if (onProgress) onProgress(phase, current, total, detail);
  };

  const log = {
    startedAt: new Date().toISOString(),
    completedAt: null,
    fixed: [],
    alreadyCorrect: 0,
    noModelSubType: 0,
    errors: [],
    status: 'in_progress',
  };

  try {
    progress('fetch', 0, 2, 'Loading models and assets from AssetPro...');
    const opts = { headers: { 'Accept': '*/*' } };
    const [modelRes, assetRes] = await Promise.all([
      apiGet('/asset-model', opts),
      apiGet('/asset/asset-all', opts),
    ]);
    const models = decodeEntityNames(extractArray(modelRes));
    const assets = extractArray(assetRes);
    progress('fetch', 2, 2, `Loaded ${models.length} models, ${assets.length} assets`);

    // Build model lookup
    const modelById = {};
    models.forEach(m => { modelById[m._id] = m; });

    // Find assets with mismatched equipmentSubType
    const toFix = [];
    let alreadyCorrect = 0;
    let noModelSubType = 0;

    for (const asset of assets) {
      const modelId = typeof asset.assetModel === 'string' ? asset.assetModel : asset.assetModel?._id;
      if (!modelId) continue;
      const model = modelById[modelId];
      if (!model) continue;
      const expectedSubId = extractId(model.assetSubType);
      if (!expectedSubId) { noModelSubType++; continue; }
      const currentSubId = extractId(asset.equipmentSubType);
      if (currentSubId === expectedSubId) {
        alreadyCorrect++;
      } else {
        toFix.push({ asset, modelId, modelName: model.name || modelId, expectedSubId, currentSubId });
      }
    }

    log.alreadyCorrect = alreadyCorrect;
    log.noModelSubType = noModelSubType;

    if (toFix.length === 0) {
      log.completedAt = new Date().toISOString();
      log.status = 'completed';
      progress('done', 1, 1, `All ${alreadyCorrect} assets already have correct subtypes. Nothing to fix.`);
      return log;
    }

    progress('repair', 0, toFix.length, `Fixing ${toFix.length} assets with stale subtypes...`);

    await batchExecute(toFix, async (item) => {
      try {
        const asset = item.asset;
        const fd = buildFormData({
          assetModel: item.modelId,
          equipmentSubType: item.expectedSubId,
          assetCode: asset.assetCode || '',
          ownerCode: asset.ownerCode || '',
          rate: asset.rate !== undefined ? asset.rate : 1,
          location: extractId(asset.location),
          client: extractId(asset.client),
          owner: extractId(asset.owner),
          leasableInfo: asset.leasableInfo || '[object Object]',
          speedLimit: asset.speedLimit !== undefined ? asset.speedLimit : 30,
          documentData: [],
          documentIds: [],
        });

        const res = await apiPutFormData(`/asset/${asset._id}`, fd);
        if (!res.success) throw new Error(res.error);

        log.fixed.push({
          assetId: asset._id,
          assetCode: asset.assetCode || '',
          model: item.modelName,
          oldSubType: item.currentSubId,
          newSubType: item.expectedSubId,
        });
        return res;
      } catch (err) {
        log.errors.push({
          assetId: item.asset._id,
          assetCode: item.asset.assetCode || '',
          model: item.modelName,
          error: err.message,
        });
        return { success: false, error: err.message };
      }
    }, 10, (phase, current, total, detail) => {
      progress('repair', current, total, `Fixing asset subtypes (${current}/${total})...`);
    }, 'repair');

    log.completedAt = new Date().toISOString();
    log.status = log.errors.length === 0 ? 'completed' : 'partial_failure';

    const msg = `Fixed ${log.fixed.length} assets, ${alreadyCorrect} already correct, ${log.errors.length} errors`;
    progress('done', 1, 1, msg);
    console.log(`[repairAssetSubTypes] ${msg}`);

    return log;

  } catch (err) {
    log.completedAt = new Date().toISOString();
    log.status = 'failed';
    log.fatalError = err.message;
    progress('done', 0, 1, `Fatal error: ${err.message}`);
    return log;
  }
}
